/**
 * Animation hooks for purposeful, restrained motion
 */

import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Hook to trigger animation when element enters viewport
 */
export function useInView<T extends HTMLElement>(
  options: IntersectionObserverInit = { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
): [React.RefObject<T>, boolean] {
  const ref = useRef<T>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.unobserve(element);
      }
    }, options);

    observer.observe(element);
    return () => observer.disconnect();
  }, [options]);

  return [ref as React.RefObject<T>, isInView];
}

/**
 * Hook for staggered children animations
 */
export function useStaggeredAnimation(
  itemCount: number,
  baseDelay: number = 50
): number[] {
  return Array.from({ length: itemCount }, (_, i) => i * baseDelay);
}

/**
 * Hook for animated counter
 */
export function useAnimatedCounter(
  target: number,
  duration: number = 1000,
  startOnView: boolean = true
): [number, React.RefObject<HTMLSpanElement>] {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!startOnView) {
      animateCounter();
      return;
    }

    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated.current) {
        hasAnimated.current = true;
        animateCounter();
        observer.unobserve(element);
      }
    }, { threshold: 0.5 });

    observer.observe(element);
    return () => observer.disconnect();

    function animateCounter() {
      const startTime = performance.now();
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Ease out quart
        const easeProgress = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(easeProgress * target));

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setCount(target);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [target, duration, startOnView]);

  return [count, ref as React.RefObject<HTMLSpanElement>];
}

/**
 * Hook for smooth hover state
 */
export function useHover<T extends HTMLElement>(): [
  React.RefObject<T>,
  boolean,
  { onMouseEnter: () => void; onMouseLeave: () => void }
] {
  const ref = useRef<T>(null);
  const [isHovered, setIsHovered] = useState(false);

  const handlers = {
    onMouseEnter: useCallback(() => setIsHovered(true), []),
    onMouseLeave: useCallback(() => setIsHovered(false), []),
  };

  return [ref as React.RefObject<T>, isHovered, handlers];
}

/**
 * Hook for page load animation sequence
 */
export function usePageLoadAnimation(): boolean {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(() => setIsLoaded(true), 50);
    return () => clearTimeout(timer);
  }, []);

  return isLoaded;
}
